"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefCallback,
} from "react";
import { Camera, CameraOff, ShieldCheck } from "lucide-react";

import { useLanguage } from "@/contexts/LanguageContext";
import {
  getInterviewRuntimeContent,
  interpolateInterviewRuntime,
  type InterviewRuntimeCopy,
} from "@/i18n/interviewRuntimeContent";
import {
  EMPTY_VISUAL_DELIVERY_SUMMARY,
  nextVisualDeliveryPerformanceState,
  VISUAL_DELIVERY_FRAME_INTERVAL_MS,
  type VisualDeliveryPerformanceState,
  type VisualDeliverySummary,
} from "@/lib/interview/v2/visualDelivery";

type VisualDeliveryStatus =
  | "off"
  | "requesting"
  | "loading"
  | "ready"
  | "active"
  | "suspended"
  | "complete"
  | "permission-denied"
  | "unsupported"
  | "disabled";

type VisualDeliveryWorkerMessage =
  | { type: "ready"; delegate: "GPU" | "CPU" }
  | { type: "summary"; summary: VisualDeliverySummary; processingMs: number }
  | { type: "error"; code: "initialization_failed" | "processing_failed" };

export type VisualDeliveryController = {
  enabled: boolean;
  status: VisualDeliveryStatus;
  statusMessage: string;
  summary: VisualDeliverySummary;
  attachVideoElement: RefCallback<HTMLVideoElement>;
  enable: () => Promise<void>;
  disable: (discardSummary?: boolean) => void;
  beginSession: () => void;
  finishSession: () => void;
};

type UseVisualDeliveryCoachOptions = {
  suspendAnalysis: boolean;
  suspensionReason?: string;
};

const INITIAL_PERFORMANCE_STATE: VisualDeliveryPerformanceState = {
  intervalMs: VISUAL_DELIVERY_FRAME_INTERVAL_MS,
  slowFrameStrikes: 0,
  disabled: false,
};

const permissionErrorMessage = (error: unknown, copy: InterviewRuntimeCopy) => {
  if (error instanceof DOMException && error.name === "NotAllowedError") {
    return copy.visualPermissionDenied;
  }
  return copy.visualCameraFailed;
};

function localizedVisualTips(
  summary: VisualDeliverySummary,
  copy: InterviewRuntimeCopy,
): string[] {
  if (summary.framesAnalyzed < 10) return [copy.visualTipInsufficient];
  const tips: string[] = [];
  if (summary.wellFramedPercent < 75) tips.push(copy.visualTipFraming);
  if (summary.cameraFacingPercent < 65) tips.push(copy.visualTipCamera);
  if (summary.uprightPosturePercent < 75) tips.push(copy.visualTipPosture);
  if (summary.averageHeadMovement === "high") tips.push(copy.visualTipMovement);
  return tips.length ? tips : [copy.visualTipGood];
}

export async function requestVisualDeliveryCamera(
  mediaDevices: Pick<MediaDevices, "getUserMedia">,
) {
  return mediaDevices.getUserMedia({
    video: {
      width: { ideal: 320, max: 640 },
      height: { ideal: 240, max: 480 },
      frameRate: { ideal: 15, max: 15 },
      facingMode: "user",
    },
    audio: false,
  });
}

export function useVisualDeliveryCoach({
  suspendAnalysis,
  suspensionReason,
}: UseVisualDeliveryCoachOptions): VisualDeliveryController {
  const { locale } = useLanguage();
  const copy = getInterviewRuntimeContent(locale);
  const effectiveSuspensionReason = suspensionReason || copy.visualPausedResources;
  const [enabled, setEnabled] = useState(false);
  const [status, setStatus] = useState<VisualDeliveryStatus>("off");
  const [statusMessage, setStatusMessage] = useState(copy.visualOff);
  const [summary, setSummary] = useState<VisualDeliverySummary>(
    EMPTY_VISUAL_DELIVERY_SUMMARY,
  );
  const workerRef = useRef<Worker | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const captureTimerRef = useRef<number | null>(null);
  const performanceResumeTimerRef = useRef<number | null>(null);
  const sessionActiveRef = useRef(false);
  const workerReadyRef = useRef(false);
  const enabledRef = useRef(false);
  const inFlightRef = useRef(false);
  const suspensionRef = useRef(suspendAnalysis);
  const suspensionReasonRef = useRef(effectiveSuspensionReason);
  const performanceStateRef = useRef(INITIAL_PERFORMANCE_STATE);
  const performanceEpisodesRef = useRef(0);
  const processingErrorsRef = useRef(0);
  const latestSummaryRef = useRef(EMPTY_VISUAL_DELIVERY_SUMMARY);
  const lastSummaryRenderRef = useRef(0);
  const permissionAttemptRef = useRef(0);

  const clearCaptureTimer = useCallback(() => {
    if (captureTimerRef.current !== null) {
      window.clearTimeout(captureTimerRef.current);
      captureTimerRef.current = null;
    }
  }, []);

  const stopResources = useCallback(() => {
    clearCaptureTimer();
    if (performanceResumeTimerRef.current !== null) {
      window.clearTimeout(performanceResumeTimerRef.current);
      performanceResumeTimerRef.current = null;
    }
    inFlightRef.current = false;
    workerReadyRef.current = false;
    if (workerRef.current) {
      workerRef.current.postMessage({ type: "stop" });
      workerRef.current.terminate();
      workerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoElementRef.current) videoElementRef.current.srcObject = null;
  }, [clearCaptureTimer]);

  const disableForPerformance = useCallback(() => {
    setSummary(latestSummaryRef.current);
    stopResources();
    sessionActiveRef.current = false;
    enabledRef.current = false;
    setEnabled(false);
    setStatus("disabled");
    setStatusMessage(copy.visualPerformanceDisabled);
  }, [copy.visualPerformanceDisabled, stopResources]);

  const disableForInitialization = useCallback(() => {
    stopResources();
    sessionActiveRef.current = false;
    enabledRef.current = false;
    setEnabled(false);
    setStatus("disabled");
    setStatusMessage(copy.visualModelFailed);
  }, [copy.visualModelFailed, stopResources]);

  const scheduleFrameRef = useRef<() => void>(() => undefined);

  const captureFrame = useCallback(async () => {
    captureTimerRef.current = null;
    if (
      !enabledRef.current ||
      !sessionActiveRef.current ||
      !workerReadyRef.current
    ) {
      return;
    }
    if (suspensionRef.current || document.visibilityState !== "visible") {
      setStatus("suspended");
      setStatusMessage(
        document.visibilityState !== "visible"
          ? copy.visualHidden
          : suspensionReasonRef.current,
      );
      captureTimerRef.current = window.setTimeout(
        scheduleFrameRef.current,
        performanceStateRef.current.intervalMs,
      );
      return;
    }

    setStatus("active");
    setStatusMessage(
      performanceStateRef.current.intervalMs > VISUAL_DELIVERY_FRAME_INTERVAL_MS
        ? copy.visualReducedRate
        : copy.visualAnalyzing,
    );

    const video = videoElementRef.current;
    if (
      !inFlightRef.current &&
      video &&
      video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
      video.videoWidth > 0
    ) {
      inFlightRef.current = true;
      try {
        const frame = await createImageBitmap(video, {
          resizeWidth: 320,
          resizeHeight: 240,
          resizeQuality: "low",
        });
        workerRef.current?.postMessage(
          { type: "frame", frame, timestampMs: performance.now() },
          [frame],
        );
      } catch {
        inFlightRef.current = false;
        processingErrorsRef.current += 1;
        if (processingErrorsRef.current >= 2) disableForPerformance();
      }
    }

    if (enabledRef.current && sessionActiveRef.current) {
      captureTimerRef.current = window.setTimeout(
        scheduleFrameRef.current,
        performanceStateRef.current.intervalMs,
      );
    }
  }, [copy.visualAnalyzing, copy.visualHidden, copy.visualReducedRate, disableForPerformance]);

  scheduleFrameRef.current = () => {
    void captureFrame();
  };

  const registerMainThreadInterference = useCallback(() => {
    if (!enabledRef.current || !sessionActiveRef.current) return;
    performanceEpisodesRef.current += 1;
    if (performanceEpisodesRef.current >= 3) {
      disableForPerformance();
      return;
    }
    clearCaptureTimer();
    setStatus("suspended");
    setStatusMessage(copy.visualPausedBriefly);
    if (performanceResumeTimerRef.current !== null) {
      window.clearTimeout(performanceResumeTimerRef.current);
    }
    performanceResumeTimerRef.current = window.setTimeout(() => {
      performanceResumeTimerRef.current = null;
      if (enabledRef.current && sessionActiveRef.current) scheduleFrameRef.current();
    }, 5_000);
  }, [clearCaptureTimer, copy.visualPausedBriefly, disableForPerformance]);

  const attachVideoElement = useCallback<RefCallback<HTMLVideoElement>>((element) => {
    videoElementRef.current = element;
    if (!element || !streamRef.current) return;
    element.srcObject = streamRef.current;
    void element.play().catch(() => undefined);
  }, []);

  const disable = useCallback(
    (discardSummary = true) => {
      permissionAttemptRef.current += 1;
      stopResources();
      enabledRef.current = false;
      sessionActiveRef.current = false;
      setEnabled(false);
      setStatus("off");
      setStatusMessage(copy.visualOff);
      if (discardSummary) {
        latestSummaryRef.current = EMPTY_VISUAL_DELIVERY_SUMMARY;
        setSummary(EMPTY_VISUAL_DELIVERY_SUMMARY);
      }
    },
    [copy.visualOff, stopResources],
  );

  const enable = useCallback(async () => {
    const attempt = permissionAttemptRef.current + 1;
    permissionAttemptRef.current = attempt;
    setEnabled(true);
    enabledRef.current = true;
    setStatus("requesting");
    setStatusMessage(copy.visualWaitingPermission);

    if (
      typeof Worker === "undefined" ||
      typeof OffscreenCanvas === "undefined" ||
      typeof createImageBitmap !== "function" ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      enabledRef.current = false;
      setEnabled(false);
      setStatus("unsupported");
      setStatusMessage(copy.visualUnsupported);
      return;
    }

    try {
      const cameraStream = await requestVisualDeliveryCamera(navigator.mediaDevices);
      if (permissionAttemptRef.current !== attempt) {
        cameraStream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = cameraStream;
      if (videoElementRef.current) {
        videoElementRef.current.srcObject = cameraStream;
        void videoElementRef.current.play().catch(() => undefined);
      }
      setStatus("loading");
      setStatusMessage(copy.visualLoading);

      const visualWorker = new Worker(
        new URL("../../workers/visual-delivery.worker.ts", import.meta.url),
        // Next.js emits this worker with a classic webpack bootstrap that uses
        // importScripts() for its MediaPipe vendor chunk. Forcing module mode
        // makes Chrome terminate the worker before it can report readiness.
        { name: "jobolar-visual-delivery" },
      );
      workerRef.current = visualWorker;
      visualWorker.onmessage = (event: MessageEvent<VisualDeliveryWorkerMessage>) => {
        const message = event.data;
        if (message.type === "ready") {
          workerReadyRef.current = true;
          processingErrorsRef.current = 0;
          setStatus(sessionActiveRef.current ? "active" : "ready");
          setStatusMessage(
            sessionActiveRef.current
              ? copy.visualAnalyzing
              : interpolateInterviewRuntime(copy.visualCameraReady, { delegate: message.delegate }),
          );
          if (sessionActiveRef.current) scheduleFrameRef.current();
          return;
        }
        if (message.type === "summary") {
          inFlightRef.current = false;
          processingErrorsRef.current = 0;
          latestSummaryRef.current = message.summary;
          performanceStateRef.current = nextVisualDeliveryPerformanceState(
            performanceStateRef.current,
            message.processingMs,
          );
          if (performanceStateRef.current.disabled) {
            disableForPerformance();
            return;
          }
          const now = performance.now();
          if (now - lastSummaryRenderRef.current >= 1_000) {
            lastSummaryRenderRef.current = now;
            setSummary(message.summary);
          }
          return;
        }
        inFlightRef.current = false;
        processingErrorsRef.current += 1;
        if (message.code === "initialization_failed") {
          disableForInitialization();
          return;
        }
        if (processingErrorsRef.current >= 2) {
          disableForPerformance();
        }
      };
      visualWorker.onerror = () => disableForInitialization();
      visualWorker.postMessage({
        type: "init",
        wasmBaseUrl: `${window.location.origin}/mediapipe/wasm`,
        modelUrl: `${window.location.origin}/mediapipe/models/pose_landmarker_lite.task`,
      });
    } catch (error) {
      stopResources();
      enabledRef.current = false;
      setEnabled(false);
      setStatus(
        error instanceof DOMException && error.name === "NotAllowedError"
          ? "permission-denied"
          : "disabled",
      );
      setStatusMessage(permissionErrorMessage(error, copy));
    }
  }, [copy, disableForInitialization, disableForPerformance, stopResources]);

  const beginSession = useCallback(() => {
    if (!enabledRef.current) return;
    sessionActiveRef.current = true;
    performanceStateRef.current = INITIAL_PERFORMANCE_STATE;
    performanceEpisodesRef.current = 0;
    processingErrorsRef.current = 0;
    latestSummaryRef.current = EMPTY_VISUAL_DELIVERY_SUMMARY;
    setSummary(EMPTY_VISUAL_DELIVERY_SUMMARY);
    workerRef.current?.postMessage({ type: "reset" });
    if (workerReadyRef.current) scheduleFrameRef.current();
  }, []);

  const finishSession = useCallback(() => {
    if (!enabledRef.current && status !== "disabled") return;
    setSummary(latestSummaryRef.current);
    stopResources();
    sessionActiveRef.current = false;
    enabledRef.current = false;
    setEnabled(false);
    if (status !== "disabled") {
      setStatus("complete");
      setStatusMessage(copy.visualFinished);
    }
  }, [copy.visualFinished, status, stopResources]);

  useEffect(() => {
    suspensionRef.current = suspendAnalysis;
    suspensionReasonRef.current = effectiveSuspensionReason;
  }, [effectiveSuspensionReason, suspendAnalysis]);

  useEffect(() => {
    if (!enabled || typeof PerformanceObserver === "undefined") {
      return;
    }
    if (!PerformanceObserver.supportedEntryTypes?.includes("longtask")) return;
    const observer = new PerformanceObserver((entries) => {
      if (entries.getEntries().some((entry) => entry.duration >= 120)) {
        registerMainThreadInterference();
      }
    });
    observer.observe({ entryTypes: ["longtask"] });
    return () => observer.disconnect();
  }, [enabled, registerMainThreadInterference]);

  useEffect(
    () => () => {
      permissionAttemptRef.current += 1;
      stopResources();
    },
    [stopResources],
  );

  return {
    enabled,
    status,
    statusMessage,
    summary,
    attachVideoElement,
    enable,
    disable,
    beginSession,
    finishSession,
  };
}

export function VisualDeliverySetup({
  controller,
}: {
  controller: VisualDeliveryController;
}) {
  const { locale } = useLanguage();
  const copy = getInterviewRuntimeContent(locale);
  const changeOptIn = (checked: boolean) => {
    if (checked) void controller.enable();
    else controller.disable(true);
  };

  return (
    <section className="rounded-xl border border-sky-200 bg-sky-50 p-4">
      <label className="flex items-start gap-3 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={controller.enabled}
          onChange={(event) => changeOptIn(event.target.checked)}
          className="mt-1"
          aria-describedby="visual-delivery-description visual-delivery-status"
        />
        <span>
          <strong className="block text-slate-950">
            {copy.visualEnable}
          </strong>
          <span id="visual-delivery-description" className="mt-1 block leading-5">
            {copy.visualDescription}
          </span>
        </span>
      </label>
      <div className="mt-3 flex items-start gap-2 text-xs text-slate-600">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
        <p>
          {copy.visualPrivacy}
        </p>
      </div>
      <p id="visual-delivery-status" aria-live="polite" className="mt-3 text-xs font-medium text-sky-900">
        {controller.statusMessage}
      </p>
      {controller.enabled ? (
        <video
          ref={controller.attachVideoElement}
          autoPlay
          muted
          playsInline
          aria-label={copy.visualPreview}
          className="mt-3 aspect-video w-full max-w-sm rounded-xl bg-slate-950 object-cover [transform:scaleX(-1)]"
        />
      ) : null}
    </section>
  );
}

export function VisualDeliverySessionPanel({
  controller,
}: {
  controller: VisualDeliveryController;
}) {
  const { locale } = useLanguage();
  const copy = getInterviewRuntimeContent(locale);
  if (!controller.enabled && controller.status !== "disabled") return null;
  return (
    <section className="rounded-2xl border border-sky-200 bg-sky-50 p-5 text-sm text-sky-950">
      <div className="flex items-center gap-2 font-semibold">
        <Camera className="h-4 w-4" /> {copy.visualTitle}
      </div>
      {controller.enabled ? (
        <video
          ref={controller.attachVideoElement}
          autoPlay
          muted
          playsInline
          aria-label={copy.visualSessionPreview}
          className="mt-3 aspect-video w-full rounded-lg bg-slate-950 object-cover [transform:scaleX(-1)]"
        />
      ) : null}
      <p className="mt-2 text-xs leading-5 text-sky-800" aria-live="polite">
        {controller.statusMessage}
      </p>
      {controller.enabled ? (
        <button
          type="button"
          onClick={() => controller.disable(true)}
          className="mt-3 inline-flex items-center gap-2 rounded-full border border-sky-300 px-3 py-1.5 text-xs font-semibold hover:bg-sky-100"
        >
          <CameraOff className="h-3.5 w-3.5" /> {copy.visualTurnOff}
        </button>
      ) : null}
    </section>
  );
}

export function VisualDeliveryResults({
  summary,
}: {
  summary: VisualDeliverySummary;
}) {
  const { locale } = useLanguage();
  const copy = getInterviewRuntimeContent(locale);
  if (!summary.framesAnalyzed) return null;
  const tips = localizedVisualTips(summary, copy);
  return (
    <section className="space-y-5 rounded-2xl border border-sky-200 bg-white p-8 shadow-sm">
      <div>
        <p className="text-sm font-semibold uppercase text-sky-700">{copy.visualTitle}</p>
        <h2 className="mt-2 text-2xl font-bold text-slate-950">{copy.visualSummaryTitle}</h2>
        <p className="mt-2 text-sm text-slate-600">
          {copy.visualSeparate}
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <VisualMetric label={copy.visualWellFramed} value={`${summary.wellFramedPercent}%`} />
        <VisualMetric label={copy.visualCameraFacing} value={`${summary.cameraFacingPercent}%`} />
        <VisualMetric label={copy.visualPosture} value={`${summary.uprightPosturePercent}%`} />
        <VisualMetric label={copy.visualHeadMovement} value={summary.averageHeadMovement} />
      </div>
      <div>
        <h3 className="font-semibold text-slate-950">{copy.visualSuggestions}</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-700">
          {tips.map((tip) => <li key={tip}>{tip}</li>)}
        </ul>
      </div>
      <p className="rounded-lg bg-slate-100 p-4 text-xs leading-5 text-slate-600">
        {interpolateInterviewRuntime(copy.visualFramesNote, {
          frames: summary.framesAnalyzed,
          seconds: summary.observedSeconds.toFixed(1),
        })}
      </p>
    </section>
  );
}

function VisualMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-sky-50 p-4">
      <p className="text-xs font-medium text-sky-800">{label}</p>
      <p className="mt-1 text-xl font-semibold capitalize text-slate-950">{value}</p>
    </div>
  );
}
